import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'

import { Observable, combineLatest, map, of } from 'rxjs'

import { AiSelectors } from '@flowx/designer/modules/new-ai'
import {
  AiAnalystUtils,
  AiProcess,
} from '@flowx/designer/modules/new-ai/agents/analyst/services/analyst.service'
import { DesignerService } from '@flowx/designer/modules/new-ai/agents/designer/services/designer.service'
import { DesignerContext } from '@flowx/designer/modules/new-ai/common/ai.dictionary'
import { selectDataModelEntitiesArray } from '@flowx/designer/shared/store/selectors/data-model.selectors'

@Injectable({ providedIn: 'root' })
export class AiContextService {
  #store = inject(Store)

  // PROCESS
  /**
   * Returns a raw snapshot of the current process designer state (nodes, sequences, swimlanes, etc.).
   * Useful when you need full, unprocessed process data for advanced operations.
   */
  getCurrentProcessSnapshot(): any {
    return AiAnalystUtils.getProcessStateSnapshot()
  }

  /**
   * Returns the AI-friendly process context built from the current process state.
   * Prefer this for sending process context to AI services.
   */
  getCurrentProcessContext(): AiProcess | null {
    return AiAnalystUtils.getInitialProcessContext()
  }

  // DATA MODEL
  /**
   * Observable of the current Data Model entities mapped for AI (OBJECT with properties, refs resolved).
   * Emits when the Data Model in the store changes.
   */
  getCurrentDataModel$(): Observable<any[]> {
    return this.#store.select(selectDataModelEntitiesArray)
  }

  /**
   * Synchronous snapshot of the current Data Model entities mapped for AI.
   * Uses selectSignal under the hood; safe for immediate reads in effects/handlers.
   */
  getCurrentDataModelSync(): any[] {
    return this.#store.selectSignal(selectDataModelEntitiesArray)()
  }

  // UI CONTEXT (all or node-specific)
  /**
   * Observable of the current sanitized UI context (entire template config) prepared for AI.
   * Emits when the UI context changes.
   */
  getCurrentUI$(): Observable<DesignerContext | null> {
    return this.#store.select(AiSelectors.currentUI)
  }

  /**
   * Synchronous snapshot of the current sanitized UI context prepared for AI.
   */
  getCurrentUISync(): DesignerContext | null {
    return this.#store.selectSignal(AiSelectors.currentUI)()
  }

  /**
   * Observable of the UI context focused on a specific node id (single template extracted and sanitized).
   * Falls back to the full UI context if nodeId is not provided or not found.
   * @param nodeId node id to focus on
   */
  getCurrentUIForNode$(nodeId: string): Observable<DesignerContext | null> {
    if (!nodeId) {
      return this.getCurrentUI$()
    }
    return this.getCurrentUI$().pipe(
      map((ctx) => {
        if (!ctx || !Array.isArray((ctx as any).templateConfig)) {
          return ctx
        }
        const clone = structuredClone ? structuredClone(ctx) : JSON.parse(JSON.stringify(ctx))
        const found = this.#findTemplateById((clone as any).templateConfig, nodeId)
        if (!found) {
          return ctx
        }
        // Return a minimal context focused on the node
        const focused: any = {
          ...clone,
          templateConfig: [found],
        }
        return DesignerService.prepareUIContextForAI(focused) as DesignerContext
      })
    )
  }

  // COMBINED CONTEXT
  /**
   * Observable of a combined AI context: { process, dataModel, ui }.
   * Optionally focuses the UI on a specific node id.
   * @param options.nodeId optional node id to focus UI context
   */
  buildCombinedContext$(options?: { nodeId?: string }): Observable<{
    process: AiProcess | null
    dataModel: any[]
    ui: DesignerContext | null
  }> {
    const ui$ = options?.nodeId ? this.getCurrentUIForNode$(options.nodeId) : this.getCurrentUI$()
    return combineLatest([
      of(this.getCurrentProcessContext()),
      this.getCurrentDataModel$(),
      ui$,
    ]).pipe(map(([process, dataModel, ui]) => ({ process, dataModel, ui })))
  }

  /**
   * Synchronous snapshot of the combined AI context: { process, dataModel, ui }.
   * Optionally focuses the UI on a specific node id.
   * @param options.nodeId optional node id to focus UI context
   */
  buildCombinedContextSync(options?: { nodeId?: string }): {
    process: AiProcess | null
    dataModel: any[]
    ui: DesignerContext | null
  } {
    const process = this.getCurrentProcessContext()
    const dataModel = this.getCurrentDataModelSync()
    const ui = options?.nodeId
      ? this.getCurrentUIForNodeSync(options.nodeId)
      : this.getCurrentUISync()
    return { process, dataModel, ui }
  }

  /**
   * Synchronous UI context focused on a specific node id (single template extracted and sanitized).
   * Falls back to the full UI context if nodeId is not provided or not found.
   * @param nodeId node id to focus on
   */
  getCurrentUIForNodeSync(nodeId: string): DesignerContext | null {
    if (!nodeId) {
      return this.getCurrentUISync()
    }
    const ctx = this.getCurrentUISync()
    if (!ctx || !Array.isArray((ctx as any).templateConfig)) {
      return ctx
    }
    const clone = structuredClone ? structuredClone(ctx) : JSON.parse(JSON.stringify(ctx))
    const found = this.#findTemplateById((clone as any).templateConfig, nodeId)
    if (!found) {
      return ctx
    }
    const focused: any = { ...clone, templateConfig: [found] }
    return DesignerService.prepareUIContextForAI(focused) as DesignerContext
  }

  // Helpers
  /**
   * Depth-first search for a template by id in a nested templateConfig tree.
   */
  #findTemplateById(templates: any[], id: string): any | null {
    if (!Array.isArray(templates)) {
      return null
    }
    for (const t of templates) {
      if (String(t?.id) === String(id)) {
        return t
      }
      const child = this.#findTemplateById(t?.templateConfig, id)
      if (child) {
        return child
      }
    }
    return null
  }
}
